import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { HelpCircle, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Card } from '../components/ui/card';
import { faqs } from '../data/mock';

const FAQ = () => {
  const categories = [
    {
      title: 'Général',
      questions: faqs.slice(0, 3)
    },
    {
      title: 'Technique & Sécurité',
      questions: faqs.slice(3, 6)
    },
    {
      title: 'Tarifs & Devis',
      questions: faqs.slice(6, 8)
    }
  ];

  return (
    <div className="min-h-screen">
      <Helmet>
        <title>FAQ - Questions fréquentes sur le nettoyage par drone | PULVISTAR</title>
        <meta name="description" content="Retrouvez les réponses à vos questions sur le nettoyage par drone PULVISTAR : fonctionnement, sécurité, tarifs, zones d'intervention et délais." />
        <link rel="canonical" href="https://pulvistar.fr/faq" />
      </Helmet>
      {/* Hero Section */}
      <section className="relative pt-32 pb-16 lg:pt-40 lg:pb-20 bg-gradient-to-br from-blue-900 to-blue-800 text-white overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-cyan-400/20 rounded-full blur-3xl"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <Badge className="mb-6 bg-cyan-500/20 text-cyan-200 hover:bg-cyan-500/30 border-0 text-base px-4 py-2">
              <HelpCircle className="w-4 h-4 mr-2" />
              Toutes vos réponses
            </Badge>
            <h1 className="text-4xl lg:text-6xl font-bold mb-6 leading-tight" style={{ fontFamily: '"Outfit", sans-serif', letterSpacing: '-0.5px' }}>
              Questions fréquentes
            </h1>
            <p className="text-xl text-blue-100 leading-relaxed" style={{ fontFamily: '"Inter", sans-serif', lineHeight: '1.7' }}>
              Tout ce que vous devez savoir sur nos services de nettoyage par drone
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 lg:py-24">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            {categories.map((category, categoryIndex) => (
              <div key={categoryIndex} className="mb-12">
                <h2 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-8" style={{ fontFamily: '"Outfit", sans-serif' }}>
                  {category.title}
                </h2>
                
                <Accordion type="single" collapsible className="space-y-4">
                  {category.questions.map((faq, index) => (
                    <AccordionItem 
                      key={index} 
                      value={`item-${categoryIndex}-${index}`} 
                      className="border border-gray-200 rounded-lg px-6 bg-white hover:shadow-md transition-shadow"
                    >
                      <AccordionTrigger className="text-left font-semibold text-gray-900 hover:text-blue-900 py-5" style={{ fontFamily: '"Outfit", sans-serif' }}>
                        {faq.question}
                      </AccordionTrigger>
                      <AccordionContent className="text-gray-600 pb-5" style={{ fontFamily: '"Inter", sans-serif', lineHeight: '1.7' }}>
                        {faq.answer}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Additional Questions Section */}
      <section className="py-16 lg:py-24 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <Card className="p-12 text-center border-gray-200 bg-white">
              <div className="w-16 h-16 bg-gradient-to-br from-cyan-100 to-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <HelpCircle className="w-8 h-8 text-blue-900" />
              </div>
              <h2 className="text-3xl font-bold text-gray-900 mb-4" style={{ fontFamily: '"Outfit", sans-serif' }}>
                Vous avez d'autres questions ?
              </h2>
              <p className="text-lg text-gray-600 mb-8" style={{ fontFamily: '"Inter", sans-serif', lineHeight: '1.7' }}>
                Notre équipe est là pour vous répondre et vous accompagner dans votre projet
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/contact">
                  <Button size="lg" className="gap-2 bg-blue-900 hover:bg-blue-800">
                    Nous contacter <ArrowRight className="w-5 h-5" />
                  </Button>
                </Link>
                <Button size="lg" variant="outline" className="gap-2" onClick={() => window.location.href = 'tel:+33184180342'}>
                  Appeler maintenant
                </Button>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* Topics Section */}
      <section className="py-16 lg:py-24">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center" style={{ fontFamily: '"Outfit", sans-serif' }}>
              Explorer par thème
            </h2>
            <div className="grid md:grid-cols-3 gap-6">
              <Link to="/services">
                <Card className="p-6 border-gray-200 hover:shadow-lg transition-all hover:-translate-y-1 cursor-pointer h-full">
                  <h3 className="font-semibold text-gray-900 mb-2" style={{ fontFamily: '"Outfit", sans-serif' }}>
                    Nos services
                  </h3>
                  <p className="text-sm text-gray-600 mb-3" style={{ fontFamily: '"Inter", sans-serif' }}>
                    Découvrez tous nos domaines d'expertise
                  </p>
                  <span className="text-cyan-600 text-sm font-medium inline-flex items-center gap-1">
                    En savoir plus <ArrowRight className="w-4 h-4" />
                  </span>
                </Card>
              </Link>
              
              <Link to="/devis">
                <Card className="p-6 border-gray-200 hover:shadow-lg transition-all hover:-translate-y-1 cursor-pointer h-full">
                  <h3 className="font-semibold text-gray-900 mb-2" style={{ fontFamily: '"Outfit", sans-serif' }}>
                    Demander un devis
                  </h3>
                  <p className="text-sm text-gray-600 mb-3" style={{ fontFamily: '"Inter", sans-serif' }}>
                    Obtenez une estimation personnalisée
                  </p>
                  <span className="text-cyan-600 text-sm font-medium inline-flex items-center gap-1">
                    Accéder au formulaire <ArrowRight className="w-4 h-4" />
                  </span>
                </Card>
              </Link>
              
              <Link to="/contact">
                <Card className="p-6 border-gray-200 hover:shadow-lg transition-all hover:-translate-y-1 cursor-pointer h-full">
                  <h3 className="font-semibold text-gray-900 mb-2" style={{ fontFamily: '"Outfit", sans-serif' }}>
                    Nous contacter
                  </h3>
                  <p className="text-sm text-gray-600 mb-3" style={{ fontFamily: '"Inter", sans-serif' }}>
                    Une question spécifique ? Écrivez-nous
                  </p>
                  <span className="text-cyan-600 text-sm font-medium inline-flex items-center gap-1">
                    Envoyer un message <ArrowRight className="w-4 h-4" />
                  </span>
                </Card>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FAQ;
